import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';

const Performance = () => {
  const { t } = useTranslation();

  const metrics = [
    {
      title: t('performance.metrics.volume.title'),
      value: t('performance.metrics.volume.value'),
      description: t('performance.metrics.volume.description'),
      icon: ChartBarIcon,
    },
    {
      title: t('performance.metrics.liquidity.title'),
      value: t('performance.metrics.liquidity.value'),
      description: t('performance.metrics.liquidity.description'),
      icon: CurrencyDollarIcon,
    },
    {
      title: t('performance.metrics.return.title'),
      value: t('performance.metrics.return.value'),
      description: t('performance.metrics.return.description'),
      icon: ArrowTrendingUpIcon,
    },
    {
      title: t('performance.metrics.risk.title'),
      value: t('performance.metrics.risk.value'),
      description: t('performance.metrics.risk.description'),
      icon: ShieldCheckIcon,
    },
  ];

  const caseStudies = [
    {
      title: t('performance.cases.exchange.title'),
      description: t('performance.cases.exchange.description'),
      metrics: [
        { label: t('performance.cases.exchange.metrics.depth'), value: '300%' },
        { label: t('performance.cases.exchange.metrics.spread'), value: '40%' },
        { label: t('performance.cases.exchange.metrics.volume'), value: '200%' }
      ],
      image: 'https://placehold.co/600x400/1E40AF/ffffff?text=Market+Making+Case'
    },
    {
      title: t('performance.cases.defi.title'),
      description: t('performance.cases.defi.description'),
      metrics: [
        { label: t('performance.cases.defi.metrics.utilization'), value: '85%' },
        { label: t('performance.cases.defi.metrics.return'), value: '50%' },
        { label: t('performance.cases.defi.metrics.risk'), value: '30%' }
      ],
      image: 'https://placehold.co/600x400/1E293B/ffffff?text=DeFi+Liquidity+Case'
    }
  ];

  const volumeData = [
    { month: '2023-10', volume: 400, target: 450 },
    { month: '2023-11', volume: 450, target: 500 },
    { month: '2023-12', volume: 500, target: 550 },
    { month: '2024-01', volume: 550, target: 600 },
    { month: '2024-02', volume: 600, target: 650 },
  ];

  const returnData = [
    { month: '2023-10', return: 12, benchmark: 10 },
    { month: '2023-11', return: 13, benchmark: 10.5 },
    { month: '2023-12', return: 14, benchmark: 11 },
    { month: '2024-01', return: 14.5, benchmark: 11.5 },
    { month: '2024-02', return: 15, benchmark: 12 },
  ];

  return (
    <>
      <Helmet>
        <title>{t('performance.meta.title')} - 9527.fi</title>
        <meta name="description" content={t('performance.meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl font-bold mb-6">{t('performance.hero.title')}</h1>
            <p className="text-xl text-gray-200">
              {t('performance.hero.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Performance Metrics */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="section-title">{t('performance.sections.metrics')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {metrics.map((metric, index) => (
              <motion.div
                key={metric.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white p-8 rounded-lg shadow-lg"
              >
                <metric.icon className="h-12 w-12 text-primary mb-6" />
                <h3 className="text-2xl font-bold mb-4">{metric.title}</h3>
                <p className="text-gray-600 mb-6">{metric.description}</p>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-700">
                    <span className="w-2 h-2 bg-primary rounded-full mr-3"></span>
                    {metric.value}
                  </li>
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="section-title">{t('performance.sections.cases')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white p-8 rounded-lg shadow-lg"
              >
                <h3 className="text-2xl font-bold mb-4">{study.title}</h3>
                <p className="text-gray-600 mb-6">{study.description}</p>
                <ul className="space-y-3">
                  {study.metrics.map((metric) => (
                    <li key={metric.label} className="flex items-center text-gray-700">
                      <span className="w-2 h-2 bg-primary rounded-full mr-3"></span>
                      {metric.label}: {metric.value}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Performance Charts */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="section-title">{t('performance.sections.charts')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white p-8 rounded-lg shadow-lg"
            >
              <h3 className="text-2xl font-bold mb-4">{t('performance.charts.volume')}</h3>
              <p className="text-gray-600 mb-6">{t('performance.charts.volumeDesc', 'Volume performance over time.')}</p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={volumeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="volume"
                      stroke="#1E40AF"
                      fill="#1E40AF"
                      fillOpacity={0.3}
                    />
                    <Area
                      type="monotone"
                      dataKey="target"
                      stroke="#3B82F6"
                      fill="#3B82F6"
                      fillOpacity={0.1}
                      strokeDasharray="5 5"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white p-8 rounded-lg shadow-lg"
            >
              <h3 className="text-2xl font-bold mb-4">{t('performance.charts.return')}</h3>
              <p className="text-gray-600 mb-6">{t('performance.charts.returnDesc', 'Return rates compared to benchmarks.')}</p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={returnData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="return"
                      stroke="#1E40AF"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="benchmark"
                      stroke="#3B82F6"
                      strokeDasharray="5 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">{t('performance.cta.title')}</h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('performance.cta.subtitle')}
          </p>
          <a
            href="mailto:stanley@9527.fi"
            className="btn btn-primary inline-block hover:scale-105 transition-transform"
          >
            {t('common.getQuote')}
          </a>
        </div>
      </section>
    </>
  );
};

export default Performance; 